import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Visibility as VisibilityIcon, LocationOn as LocationIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Edit as EditIcon } from '@mui/icons-material';
import { Shift, ShiftStatus } from '../types';
import { formatDateTime, formatTime, formatDate, isWithinShiftTimeWindow } from '../utils';
import { Switch } from '@mui/material';
import { Badge } from '../../../pages/ShiftManagement/components/ui/Badge';
import { cityOptions } from '../../../constants/enums';

interface ShiftTableViewProps {
  shifts: Shift[];
  onToggle: (shiftId: string) => void;
  onViewDetails: (shiftId: string) => void;
  onEdit?: (shiftId: string) => void;
  getStatusColor: (status: ShiftStatus) => string;
  isShiftEnabled: (shift: Shift) => boolean;
  loading?: boolean;
}

export const ShiftTableView: React.FC<ShiftTableViewProps> = ({
  shifts,
  onToggle,
  onViewDetails,
  onEdit,
  getStatusColor,
  isShiftEnabled,
  loading = false,
}) => {
  const getCityName = (shift: Shift) => {
    if (shift.cityName) return shift.cityName;
    if (shift.cityId) {
      const city = cityOptions.find(c => String(c.id) === String(shift.cityId));
      return city?.name || 'N/A';
    }
    return 'N/A';
  };

  const getNeighborhoodName = (shift: Shift) => {
    if (shift.neighborhoodName) return shift.neighborhoodName;
    if (shift.zone) return shift.zone;
    return 'N/A';
  };

  const getStatusBadgeVariant = (status: ShiftStatus): 'success' | 'secondary' | 'warning' | 'destructive' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <TableContainer className="overflow-x-auto w-full" sx={{ overflowX: 'auto', overflowY: 'visible' }}>
      <Table
        sx={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0 10px',
          '& thead th': {
            borderBottom: 'none',
            fontSize: '12px',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            backgroundColor: 'rgba(249, 250, 251, 0.8)',
          },
        }}
        aria-label="shifts table"
      >
        <TableHead>
          <TableRow className='bg-gray-400 rounded-lg'>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '15%' }}>Shift Session</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%' }}>City</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Neighborhood</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Shift Scheduler</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Start Time</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>End Time</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '6%' }}>Required Drivers</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%' }}>Coordinates</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%' }}>Status</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%' }}>Created</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%' }}>Updated</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '6%' }}>Active</TableCell>
            <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" align="right" sx={{ width: '6%' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={12} align="center" className="py-20">
                <div className="flex flex-col items-center justify-center">
                  <CircularProgress size={48} sx={{ color: '#3B82F6', mb: 2 }} />
                  <p className="text-sm font-medium text-gray-600">Loading</p>
                </div>
              </TableCell>
            </TableRow>
          ) : shifts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} align="center" className="py-12">
                <div className="text-gray-500">
                  <p className="text-sm font-medium">No shift sessions found</p>
                  <p className="text-xs text-gray-400 mt-1">Create a new shift session to get started</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            shifts.map((shift, index) => (
              <TableRow
                key={shift.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '& td, & th': { borderBottom: 'none' },
                  '& td:first-of-type, & th:first-of-type': {
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                  },
                  '& td:last-of-type, & th:last-of-type': {
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 12,
                  },
                  '&:hover': {
                    background: 'linear-gradient(90deg, rgba(75, 85, 99, 0.15) 0%, rgba(75, 85, 99, 0.08) 100%)',
                    boxShadow: '0 4px 16px rgba(75, 85, 99, 0.25)',
                    '& td:first-of-type, & th:first-of-type': {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                    '& td:last-of-type, & th:last-of-type': {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  },
                  position: 'relative',
                  transition: 'all 0.2s ease-in-out',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 24,
                    right: 24,
                    bottom: -5,
                    height: '1px',
                    backgroundColor: 'rgba(226, 232, 240, 0.9)',
                  }
                }}
                className="bg-white border border-gray-100"
              >
                <TableCell component="th" scope="row" className="py-2.5" sx={{ overflow: 'hidden' }}>
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="text-[11px] font-mono text-gray-500 uppercase tracking-wide">
                      #{shift.id}
                    </span>
                    <span className="font-semibold text-sm text-gray-900 mt-0.5 truncate block min-w-0">
                      {shift.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5">
                  <span className="text-sm text-gray-800 font-medium">{getCityName(shift)}</span>
                </TableCell>
                <TableCell className="py-2.5">
                  <span className="text-sm text-gray-800 font-medium">
                    {getNeighborhoodName(shift)}
                  </span>
                </TableCell>
                <TableCell className="py-2.5">
                  <span className="text-sm text-gray-800 font-medium">
                    {shift.shiftTemplate?.name || 'N/A'}
                  </span>
                </TableCell>
                <TableCell className="py-2.5">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800 font-medium">
                      {formatDate(shift.startTime)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTime(shift.startTime)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800 font-medium">
                      {formatDate(shift.endTime)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTime(shift.endTime)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5">
                  <span className="text-sm text-gray-800 font-medium">
                    {shift.requiredDrivers || shift.maxDrivers || 0}
                  </span>
                </TableCell>
                <TableCell className="py-2.5">
                  {shift.startLatitude && shift.startLongitude ? (
                    <Tooltip title={`Lat: ${shift.startLatitude.toFixed(4)}, Lng: ${shift.startLongitude.toFixed(4)}`} arrow>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <LocationIcon sx={{ fontSize: '14px' }} />
                        <span>{shift.startLatitude.toFixed(2)}, {shift.startLongitude.toFixed(2)}</span>
                      </div>
                    </Tooltip>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </TableCell>
                <TableCell className="py-2.5">
                  <Badge variant={getStatusBadgeVariant(shift.status)}>
                    {shift.status.charAt(0).toUpperCase() + shift.status.slice(1)}
                  </Badge>
                </TableCell>
                <TableCell className="py-2.5">
                  <span className="text-sm text-gray-800 font-medium">
                    {shift.createdAt ? formatDateTime(shift.createdAt) : 'N/A'}
                  </span>
                </TableCell>
                <TableCell className="py-2.5">
                  <span className="text-sm text-gray-800 font-medium">
                    {shift.updatedAt ? formatDateTime(shift.updatedAt) : 'N/A'}
                  </span>
                </TableCell>
                <TableCell className="py-2.5">
                  {isWithinShiftTimeWindow(shift) ? (
                    <div className="flex items-center justify-center gap-2">
                      {isShiftEnabled(shift) ? (
                        <Tooltip title="Active - Click to deactivate" arrow>
                          <div className="flex items-center gap-1.5">
                            <CheckCircleIcon sx={{ fontSize: '18px', color: '#10B981' }} />
                            <Switch
                              checked={true}
                              onChange={() => onToggle(shift.id)}
                              color="primary"
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#10B981',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#10B981',
                                },
                              }}
                            />
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Inactive - Click to activate" arrow>
                          <div className="flex items-center gap-1.5">
                            <CancelIcon sx={{ fontSize: '18px', color: '#9CA3AF' }} />
                            <Switch
                              checked={false}
                              onChange={() => onToggle(shift.id)}
                              color="primary"
                              size="small"
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </TableCell>
                <TableCell align="right" className="py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {shift.status === 'draft' && onEdit && (
                      <Tooltip title="Edit Shift Session" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(shift.id)}
                          className="hover:bg-blue-100"
                        >
                          <EditIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Details" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails(shift.id)}
                        className="text-primaryColor hover:bg-primaryColor/10"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};


